import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import { every, isEmpty, get } from 'lodash';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box, Button, WrappedTextInput } from 'components';
import { ICompleteProfileModal } from 'common/types';
import { errorMessageHandler } from 'common/utils/helpers';
import { CompleteProfileSchema } from 'common/utils/validations';
import { updateInvestorDetail } from 'services';
import { getInvestorDetails } from 'redux-modules/investor/Actions';

import styles from './styles';

type Props = {
  investor: any;
};

const BasicDetails = ({ investor }: Props) => {
  const dispatch = useDispatch();
  const classes = styles();

  const { handleSubmit, control, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(CompleteProfileSchema),
  });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [successMessage, setSuccessMessage] = React.useState<string>('');

  const onSubmit = ({
    linkedinUrl,
    crunchbaseUrl,
    anglelistUrl,
    twitterUrl,
    otherUrl,
  }: ICompleteProfileModal) => {
    setErrorMessage('');
    setSuccessMessage('');
    if (
      every(
        [
          isEmpty(linkedinUrl),
          isEmpty(twitterUrl),
          isEmpty(anglelistUrl),
          isEmpty(crunchbaseUrl),
          isEmpty(otherUrl),
        ],
        Boolean,
      )
    ) {
      setErrorMessage('Please add your social media links');
    } else {
      const payload = {
        id: get(investor, 'id'),
        linkedinUrl,
        crunchbaseUrl,
        anglelistUrl,
        twitterUrl,
        otherUrl,
      };
      setIsLoading(true);
      updateInvestorDetail(payload)
        // eslint-disable-next-line no-unused-vars
        .then((res: any) => {
          setIsLoading(false);
          setErrorMessage('');
          setSuccessMessage('Successfully updated');
          dispatch(getInvestorDetails());
          setTimeout(() => setSuccessMessage(''), 3500);
        })
        .catch((err) => {
          setIsLoading(false);
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
        });
    }
  };

  useEffect(() => {
    if (!isEmpty(get(investor, 'id'))) {
      setValue('anglelistUrl', get(investor, 'anglelistUrl'));
      setValue('crunchbaseUrl', get(investor, 'crunchbaseUrl'));
      setValue('linkedinUrl', get(investor, 'linkedinUrl'));
      setValue('twitterUrl', get(investor, 'twitterUrl'));
      setValue('otherUrl', get(investor, 'otherUrl'));
    }
  }, [investor]);

  return (
    <Box>
      <Typography className={classes.bDHead}>Basic Details -</Typography>
      {!isEmpty(errorMessage) && (
        <Typography className={classes.errorText} variant="body1">
          {errorMessage}
        </Typography>
      )}
      {!isEmpty(successMessage) && (
        <Typography className={classes.successText} variant="body1">
          {successMessage}
        </Typography>
      )}
      <form onSubmit={handleSubmit(onSubmit)} data-testid="completeProfileForm">
        <Typography className={classes.bDLabelText}>Name</Typography>
        <Typography className={classes.bDName}>{investor.name}</Typography>
        <Typography className={classes.bDLabelText}>Email</Typography>
        <Typography className={classes.bDEmail}>{investor.email}</Typography>
        <Typography className={classes.bDLabelText}>Social Links</Typography>
        <Controller
          name={'anglelistUrl'}
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box className={classes.controllerBox}>
              <WrappedTextInput
                type="text"
                placeholder="Enter AngelList Url"
                className={classes.textSocialInput}
                InputProps={{
                  classes: { root: classes.inputRoot },
                }}
                InputLabelProps={{
                  classes: {
                    root: classes.labelRoot,
                    focused: classes.labelFocused,
                  },
                }}
                value={value}
                onChange={(e: any) => {
                  onChange(get(e, 'target.value'));
                }}
                error={!!error?.message}
              />
              {error?.message && (
                <span className={classes.errorUrl}>{error?.message}</span>
              )}
            </Box>
          )}
        />
        <Controller
          name={'crunchbaseUrl'}
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box className={classes.controllerBox}>
              <WrappedTextInput
                type="text"
                placeholder="Enter Crunchbase Url"
                className={classes.textSocialInput}
                InputProps={{
                  classes: { root: classes.inputRoot },
                }}
                InputLabelProps={{
                  classes: {
                    root: classes.labelRoot,
                    focused: classes.labelFocused,
                  },
                }}
                value={value}
                onChange={(e: any) => {
                  onChange(get(e, 'target.value'));
                }}
                error={!!error?.message}
              />
              {error?.message && (
                <span className={classes.errorUrl}>{error?.message}</span>
              )}
            </Box>
          )}
        />
        <Controller
          name={'linkedinUrl'}
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box className={classes.controllerBox}>
              <WrappedTextInput
                type="text"
                placeholder="Enter Linkedin Url"
                className={classes.textSocialInput}
                InputProps={{
                  classes: { root: classes.inputRoot },
                }}
                InputLabelProps={{
                  classes: {
                    root: classes.labelRoot,
                    focused: classes.labelFocused,
                  },
                }}
                value={value}
                onChange={(e: any) => {
                  onChange(get(e, 'target.value'));
                }}
                error={!!error?.message}
              />
              {error?.message && (
                <span className={classes.errorUrl}>{error?.message}</span>
              )}
            </Box>
          )}
        />
        <Controller
          name={'twitterUrl'}
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box className={classes.controllerBox}>
              <WrappedTextInput
                type="text"
                placeholder="Enter Twitter Url"
                className={classes.textSocialInput}
                InputProps={{
                  classes: { root: classes.inputRoot },
                }}
                InputLabelProps={{
                  classes: {
                    root: classes.labelRoot,
                    focused: classes.labelFocused,
                  },
                }}
                value={value}
                onChange={(e: any) => {
                  onChange(get(e, 'target.value'));
                }}
                error={!!error?.message}
              />
              {error?.message && (
                <span className={classes.errorUrl}>{error?.message}</span>
              )}
            </Box>
          )}
        />
        <Controller
          name={'otherUrl'}
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Box className={classes.controllerBox}>
              <WrappedTextInput
                type="text"
                placeholder="Other Url"
                className={classes.textSocialInput}
                InputProps={{
                  classes: { root: classes.inputRoot },
                }}
                InputLabelProps={{
                  classes: {
                    root: classes.labelRoot,
                    focused: classes.labelFocused,
                  },
                }}
                value={value}
                onChange={(e: any) => {
                  onChange(get(e, 'target.value'));
                }}
                error={!!error?.message}
              />
              {error?.message && (
                <span className={classes.errorUrl}>{error?.message}</span>
              )}
            </Box>
          )}
        />
        <Box>
          <Button
            type="submit"
            name="Edit"
            className={classes.submitBtn}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </Box>
      </form>
    </Box>
  );
};
export default BasicDetails;
